import { PageContainer } from "../../components/pageContainer/PageContainer";
import { useNavigate } from "react-router";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import { DISTRIBUTION } from "../../constants/routes";
import dayjs from "dayjs";
import {
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  Icon,
  createSellarColumnHelper,
} from "@sellar-io/components";
import { pluralize } from "@sellar-io/common";
import { distributionCollectionStatus } from "./DistributionCollectionListItem.shared";
import { Alert } from "../../components/alert/Alert";
import { TableInfinite } from "../../components/table/TableInfinite";

import { SellarDistributionCollectionGetResponseType } from "@sellar-io/api-v2/types";
import { SpinnerLegacy } from "../../components";
import { CollectionOrderDrawer } from "../collections/CollectionOrderDrawer";
import { useState } from "react";
import { downloadPDF } from "../../utils/downloadHelpers";
import {
  CollectionPickingListReference,
  useSellarDownloadCollectionPickingList,
} from "./hooks/useSellarDownloadCollectionPickingList";
import {
  InlineTab,
  InlineTabGroup,
  InlineTabList,
  InlineTabPanel,
  InlineTabPanels,
} from "../../components/inlineTab/InlineTab";

export const DistributionDetailsContent = ({
  collection,
  isLoading,
  pickingListReference,
}: {
  collection?: SellarDistributionCollectionGetResponseType;
  isLoading: boolean;
  pickingListReference: CollectionPickingListReference;
}) => {
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();

  const [orderDrawerActive, setOrderDrawerActive] = useState(false);
  const [orderId, setOrderId] = useState<number>();
  const [pickingListView, setPickingListView] = useState<"sku" | "order">(
    "sku",
  );
  const downloadPickingListMutation = useSellarDownloadCollectionPickingList();

  const handlePrint = () => {
    if (!collection) return;

    downloadPickingListMutation.mutate(
      { reference: pickingListReference, view: pickingListView },
      {
        onSuccess: (base64Pdf) => {
          downloadPDF(
            `Picking List - SD Collection - ${dayjs(collection.collectionDate).format("YYYY-MM-DD")}.pdf`,
            base64Pdf,
          );
        },
      },
    );
  };

  const columnHelper =
    createSellarColumnHelper<
      SellarDistributionCollectionGetResponseType["skus"][number]
    >();

  const skuColumns = [
    columnHelper.accessor("sku", {
      header: "SKU",
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue() || "-";
        return <span className="wrap-anywhere">{value}</span>;
      },
    }),
    columnHelper.accessor("name", {
      header: "Product name",
      enableSorting: false,
      cell: (info) => {
        const productName = info.getValue();
        const name = productName.split(" • ")[0];
        return <span className="wrap-anywhere">{name}</span>;
      },
    }),
    columnHelper.accessor("name", {
      header: "Format",
      enableSorting: false,
      cell: (info) => {
        const productName = info.getValue();
        const container = productName.split(" • ")[1];
        return container || "-";
      },
    }),
    columnHelper.accessor("quantity", {
      header: "Quantity",
      enableSorting: false,
    }),
    columnHelper.accessor("orderIds", {
      header: "Orders",
      enableSorting: false,
      cell: ({ getValue }) => {
        const orderIds = getValue();
        const nbOrders = orderIds.length;
        return (
          <span className="flex cursor-pointer gap-1">
            {getValue().map((id, index) => (
              <span
                key={id}
                className="underline"
                onClick={() => {
                  setOrderId(id);
                  setOrderDrawerActive(true);
                }}
              >
                #{id}
                {index < nbOrders - 1 ? "," : ""}
              </span>
            ))}
          </span>
        );
      },
    }),
  ];

  const orderColumnHelper =
    createSellarColumnHelper<
      SellarDistributionCollectionGetResponseType["orderItems"][number]
    >();

  const orderColumns = [
    orderColumnHelper.accessor("orderId", {
      header: "Order ID",
      enableSorting: false,
      cell: ({ getValue }) => (
        <button
          type="button"
          className="cursor-pointer underline"
          onClick={() => {
            setOrderId(getValue());
            setOrderDrawerActive(true);
          }}
        >
          #{getValue()}
        </button>
      ),
    }),
    orderColumnHelper.accessor("customerName", {
      header: "Customer",
      enableSorting: false,
      cell: ({ getValue }) => (
        <span className="wrap-anywhere">{getValue()}</span>
      ),
    }),
    orderColumnHelper.accessor("sku", {
      header: "SKU",
      enableSorting: false,
      cell: ({ getValue }) => (
        <span className="wrap-anywhere">{getValue() || "-"}</span>
      ),
    }),
    orderColumnHelper.accessor("name", {
      header: "Product name",
      enableSorting: false,
      cell: ({ getValue }) => (
        <span className="wrap-anywhere">{getValue().split(" • ")[0]}</span>
      ),
    }),
    orderColumnHelper.accessor("name", {
      id: "format",
      header: "Format",
      enableSorting: false,
      cell: ({ getValue }) => getValue().split(" • ")[1] || "-",
    }),
    orderColumnHelper.accessor("quantity", {
      header: "Quantity",
      enableSorting: false,
    }),
  ];

  const collectionTitle = () => {
    if (["dispatched", "delivered"].includes(collection?.status ?? ""))
      return "Collected";
    return "Estimated collection";
  };

  return (
    <>
      <NavigationBar
        title="Collection details"
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={isTwoPanel ? undefined : () => navigate(DISTRIBUTION)}
      />
      <PageContainer className="flex flex-col gap-2">
        {isLoading || !collection ? (
          <SpinnerLegacy />
        ) : (
          <>
            <Card className="bg-white">
              <CardHeader className="flex items-center justify-between gap-4 bg-white p-4">
                <div className="flex flex-col">
                  <H5>Sellar Distribution</H5>
                  <H6 className="text-zinc-500">
                    {collection.nbOfOrders}{" "}
                    {pluralize("order", collection.nbOfOrders)} •{" "}
                    {collection.nbOfSkus}{" "}
                    {pluralize("SKU", collection.nbOfSkus)}
                  </H6>
                </div>
                {distributionCollectionStatus(collection.status)}
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex flex-col items-center justify-between gap-2 rounded-lg bg-zinc-100 p-4">
                  <img
                    alt={""}
                    src="https://sellar.imgix.net/sellar-image-assets/sellar-logo-gradient.svg"
                    className={"h-10 w-10"}
                  />
                  <span className="text-lg font-semibold">
                    Sellar Distribution
                  </span>
                  <div className="flex gap-4">
                    <div className="flex flex-col justify-between gap-1 text-center">
                      <span>{collectionTitle()}</span>
                      <span className="text-lg font-semibold">
                        {dayjs(collection.collectionDate).format("DD/MM/YYYY")}
                      </span>
                    </div>
                    {collection.status === "delivered" && (
                      <div className="flex flex-col justify-between gap-1 text-center">
                        <span>Delivered</span>
                        <span className="text-lg font-semibold">
                          {dayjs(collection.deliveryDate).format("DD/MM/YYYY")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="flex items-center justify-between bg-white p-4">
                <H5>Picking list</H5>
                <DataButton
                  border={true}
                  onClick={handlePrint}
                  loading={downloadPickingListMutation.isPending}
                >
                  Print PDF <Icon icon="PRINTER" size={0.7} />
                </DataButton>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 p-4">
                {collection.status === "processing" && (
                  <Alert
                    type="information"
                    title="Order processing"
                    message="The Sellar Distribution team have received your order and will process it for collection shortly."
                  />
                )}
                {collection.status === "pending" && (
                  <Alert
                    type="information"
                    title="Picking list not final"
                    message="This Sellar Distribution collection slot has not yet been confirmed by the team. We will send you an email once collection is confirmed."
                  />
                )}
                <div>
                  <InlineTabGroup
                    onChange={(index) =>
                      setPickingListView(index === 0 ? "sku" : "order")
                    }
                  >
                    <InlineTabList>
                      <InlineTab>View by SKU</InlineTab>
                      <InlineTab>View by Order</InlineTab>
                    </InlineTabList>
                    <InlineTabPanels className="mt-4">
                      <InlineTabPanel>
                        <TableInfinite
                          columns={skuColumns}
                          data={collection.skus}
                          infiniteQuery={{
                            data: collection.skus,
                            isFetching: false,
                            totalCount: collection.skus.length,
                          }}
                          noResultsText="No products found."
                          gridClasses={`grid-cols-[minmax(200px,_1fr)_minmax(200px,_1fr)_minmax(200px,_1fr)_minmax(50px,_1fr)_minmax(70px,_1fr)]`}
                          roundedTop={true}
                        />
                      </InlineTabPanel>
                      <InlineTabPanel>
                        <TableInfinite
                          columns={orderColumns}
                          data={collection.orderItems}
                          infiniteQuery={{
                            data: collection.orderItems,
                            isFetching: false,
                            totalCount: collection.orderItems.length,
                          }}
                          noResultsText="No products found."
                          gridClasses={`grid-cols-[minmax(100px,_1fr)_minmax(160px,_1fr)_minmax(100px,_1fr)_minmax(170px,_1fr)_minmax(130px,_1fr)_minmax(80px,_1fr)]`}
                          roundedTop={true}
                        />
                      </InlineTabPanel>
                    </InlineTabPanels>
                  </InlineTabGroup>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </PageContainer>
      <CollectionOrderDrawer
        active={orderDrawerActive}
        setActive={setOrderDrawerActive}
        orderId={orderId}
      />
    </>
  );
};
